/**
 * Check Returning Patient Actor
 *
 * Calls the returning-patient lookup endpoint to determine whether the
 * patient entering phone + DOB is already in the system.
 *
 * The underlying server action (matchByPhoneAndDob in consumer-actions.ts)
 * makes a real authenticated call to USERS_API_URL consumer/match/by-phone-and-dob.
 * Note: that server action silently coerces a malformed/incomplete backend
 * response to NO_PATIENTS_MATCHED (logWarn only) — so a contract drift looks
 * like "no match" one layer before this actor ever sees it.
 *
 * The actor's error metadata (errorCodes/errorSchema) is still unfilled — that
 * is a metadata gap, not a missing backend.
 */

import { fromPromise } from "xstate";
import { z } from "zod";
import { trackNetworkRequest } from "../../lib/network-diagnostics";
import { matchByPhoneAndDob } from "../../server-actions/consumer-actions";
import type { MatchByPhoneAndDobResponse } from "../../server-actions/consumer-actions";
import { fetchUserEmail } from "../../server-actions/user-actions";
import type { ActorMetadata, ActorWithMetadata } from "./send-sms.actor";

/**
 * Input Schema — what this actor needs from machine context.
 */
const CheckReturningPatientInputSchema = z.object({
  phoneNumber: z.string(),
  dateOfBirth: z.string(),
  orgId: z.number(),
});

type CheckReturningPatientInput = z.infer<
  typeof CheckReturningPatientInputSchema
>;

/**
 * Success Schema — what this actor returns on success.
 */
const CheckReturningPatientSuccessSchema = z.object({
  message: z.enum([
    "PATIENT_MATCHED",
    "NO_PATIENTS_MATCHED",
    "MULTIPLE_PATIENTS_MATCHED",
  ]),
  consumer: z
    .object({
      id: z.string(),
      userId: z.string(),
      firstname: z.string(),
      lastname: z.string(),
      dob: z.string(),
      sex: z.string().nullable(),
      streetAddress: z.string().nullable(),
      city: z.string().nullable(),
      state: z.string().nullable(),
      zip: z.string().nullable(),
      addressLine2: z.string().nullable(),
      ehrPatientId: z.string().nullable(),
      ehrEnterpriseId: z.string().nullable(),
      email: z.string().nullable(),
      insurances: z.array(z.any()).optional(),
      hipaaSignDate: z.string().nullable(),
      // Emergency contact
      emergencyContactFirstName: z.string().nullable().optional(),
      emergencyContactLastName: z.string().nullable().optional(),
      emergencyContactPhone: z.string().nullable().optional(),
      emergencyContactRelationship: z.string().nullable().optional(),
      // PCP
      pcpFirstName: z.string().nullable().optional(),
      pcpLastName: z.string().nullable().optional(),
      pcpPhone: z.string().nullable().optional(),
      pcpAddress: z
        .object({
          streetAddress: z
            .union([
              z.string(),
              z.object({
                streetAddress: z.string().optional(),
                city: z.string().optional(),
                state: z.string().optional(),
                zip: z.string().optional(),
              }),
            ])
            .optional(),
          city: z.string().optional(),
          state: z.string().optional(),
          zip: z.string().optional(),
        })
        .nullable()
        .optional()
        .transform(
          (
            val,
          ):
            | {
                streetAddress?: string;
                city?: string;
                state?: string;
                zip?: string;
              }
            | null
            | undefined => {
            if (!val) return val;
            if (val.streetAddress && typeof val.streetAddress === "object") {
              const inner = val.streetAddress;
              const flat = {
                streetAddress: inner.streetAddress,
                city: inner.city,
                state: inner.state,
                zip: inner.zip,
              };
              return Object.values(flat).some((v) => v !== undefined)
                ? flat
                : null;
            }
            const flat = {
              streetAddress: val.streetAddress as string | undefined,
              city: val.city,
              state: val.state,
              zip: val.zip,
            };
            return Object.values(flat).some((v) => v !== undefined)
              ? flat
              : null;
          },
        ),
      // Pharmacy
      pharmacyName: z.string().nullable().optional(),
      pharmacyPhone: z.string().nullable().optional(),
      pharmacyAddress: z
        .object({
          streetAddress: z.string().optional(),
          city: z.string().optional(),
          state: z.string().optional(),
          zip: z.string().optional(),
        })
        .nullable()
        .optional(),
      // Referral
      referralSource: z.string().nullable().optional(),
    })
    .nullable(),
  // Surfaced for direct use by sendSMSActor in the PATIENT_MATCHED path
  userId: z.string().optional(),
  consumerId: z.string().optional(),
  // Surfaced for the MULTIPLE_PATIENTS_MATCHED disambiguation path
  correlationId: z.string().optional(),
  disambiguationField: z.string().optional(),
});

type CheckReturningPatientSuccess = z.infer<
  typeof CheckReturningPatientSuccessSchema
>;

/**
 * Actor Implementation
 *
 * No error path is needed while the server action is a mock (it always
 * resolves). The structure intentionally mirrors other actors so that an
 * onError transition can be wired in the arc once the real API lands.
 */
const checkReturningPatientActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: CheckReturningPatientInput;
  }): Promise<CheckReturningPatientSuccess> => {
    const validated = CheckReturningPatientInputSchema.safeParse(input);

    if (!validated.success) {
      const error = new Error(
        `checkReturningPatient validation failed: ${validated.error.message}`,
      );
      throw error; // XState onError
    }

    const { phoneNumber, dateOfBirth, orgId } = validated.data;

    // Strip the +1 country code prefix added by ContactInput's E.164 transform.
    // The match/by-phone-and-dob endpoint expects a 10-digit US number.
    const normalizedPhone = phoneNumber.replace(/^\+1/, "");

    const tracker = trackNetworkRequest("matchByPhoneAndDob", { orgId });
    try {
      const response: MatchByPhoneAndDobResponse = await matchByPhoneAndDob(
        normalizedPhone,
        dateOfBirth,
        orgId,
      );
      tracker.success();

      const consumer = response.data.consumer;

      // Fetch email from User record (not on Consumer).
      // On fetch failure, leave email off — verify mode will prompt for it.
      const emailResult = consumer?.userId
        ? await fetchUserEmail(consumer.userId)
        : null;
      const resolvedEmail =
        emailResult && emailResult.status === "ok" ? emailResult.email : null;
      const consumerWithEmail = consumer
        ? { ...consumer, ...(resolvedEmail ? { email: resolvedEmail } : {}) }
        : consumer;

      return {
        message: response.data.message,
        consumer: consumerWithEmail,
        // Surface IDs for direct use by sendSMSActor in the PATIENT_MATCHED path
        userId: consumer?.userId ?? undefined,
        consumerId: consumer?.id ?? undefined,
        // Surface disambiguation fields for the MULTIPLE_PATIENTS_MATCHED path
        correlationId: response.data.correlationId,
        disambiguationField: response.data.disambiguationField,
      };
    } catch (error) {
      tracker.error(error);
      throw error;
    }
  },
);

/**
 * Check Returning Patient Actor with Metadata
 */
export const checkReturningPatientActor: ActorWithMetadata = {
  impl: checkReturningPatientActorImpl,
  metadata: {
    displayName: "checkReturningPatient",
    description:
      "Looks up whether the patient (phone + DOB) already exists in the system",
    category: "auth",
    retryable: false,
    timeout: 10000, // 10 seconds
    errorCodes: [],
    inputSchema: CheckReturningPatientInputSchema,
    successSchema: CheckReturningPatientSuccessSchema,
    errorSchema: z.object({}),
    onSuccess: (result: unknown) => {
      const r = result as CheckReturningPatientSuccess;
      return {
        lookupResult: { message: r.message, consumer: r.consumer },
        // Surface consumer IDs for direct use by sendSMSActor in the PATIENT_MATCHED path
        userId: r.consumer?.userId ?? undefined,
        consumerId: r.consumer?.id ?? undefined,
        // Surface disambiguation fields for the MULTIPLE_PATIENTS_MATCHED path
        correlationId: r.correlationId,
        disambiguationField: r.disambiguationField,
      };
    },
    onError: (_error: unknown) => {
      return {};
    },
  } satisfies ActorMetadata,
};
